import { useState } from "react";
import { GitPullRequest, Pencil } from "lucide-react";
import { toast } from "sonner";

import { Input } from "@monorepo-apps/ui/src/components/input";
import { cn } from "@monorepo-apps/ui/src/lib/utils";

import { isHttpUrl } from "@/application/use-cases/b2c/is-http-url";

/** "PR #994" for a GitHub pull URL, else a plain "Pull request". */
function prLabel(url: string): string {
  const n = /\/pull\/(\d+)/.exec(url)?.[1];
  return n ? `PR #${n}` : "Pull request";
}

/**
 * A pull request link (one repo's integration branch → its base). Always shown: the
 * link when there is one, an empty "No PR yet" slot when there isn't. Editable
 * in place for writers; saves on blur or Enter, and an empty value clears it.
 */
export function IterationPrLink({
  url,
  canWrite,
  label,
  onChange,
}: {
  url: string | null;
  canWrite: boolean;
  /** Accessible name of the link field, e.g. "yango-app pull request link". */
  label: string;
  onChange: (next: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(url ?? "");

  if (editing) {
    return (
      <form
        className="flex items-center"
        onSubmit={(e) => {
          e.preventDefault();
          (e.currentTarget.elements.namedItem("pr") as HTMLInputElement | null)?.blur();
        }}
      >
        <Input
          name="pr"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="https://github.com/…/pull/…"
          aria-label={label}
          className="font-data h-6 w-64 text-[11px]"
          onBlur={() => {
            const next = value.trim() || null;
            if (next && !isHttpUrl(next)) {
              toast.error("The PR link must start with https://");
              setEditing(false);
              return;
            }
            if (next !== url) onChange(next);
            setEditing(false);
          }}
        />
      </form>
    );
  }

  return (
    <span className="flex items-center gap-1 text-[11px]">
      <GitPullRequest
        className={cn("size-3", url ? "text-primary" : "text-muted-foreground")}
        aria-hidden
      />
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="text-primary font-semibold hover:underline"
        >
          {prLabel(url)}
        </a>
      ) : (
        <span className="text-muted-foreground italic">No PR yet</span>
      )}
      {canWrite ? (
        <button
          type="button"
          onClick={() => {
            setValue(url ?? "");
            setEditing(true);
          }}
          title={url ? "Edit the PR link" : "Add the PR link"}
          aria-label={url ? `Edit the ${label}` : `Add the ${label}`}
          className="text-muted-foreground hover:text-primary rounded p-0.5"
        >
          <Pencil className="size-3" aria-hidden />
        </button>
      ) : null}
    </span>
  );
}
